import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Pointer-first navigation for smart-TV browsers (JioSphere, Tizen, webOS...).
//
// Ye browsers remote ke D-pad ko page tak nahi bhejte — wo apna ek alag
// virtual pointer (cursor) chalate hain. Keydown events na aane se TvNavigation
// ka focus engine chalta hi nahi, aur page scroll karne ka koi tareeka nahi
// bachta. Is component se pointer hi sab kuch karta hai:
//   - pointer screen ke kinare (top/bottom/left/right) jaaye -> smooth auto-scroll
//   - mouse wheel -> smooth scroll (jahan wheel event aata ho)
//   - press-and-drag -> content scroll (momentum-free, direct)
//   - hover -> focusable card/button focus ho jaata hai (gold ring dikhe)
//
// Sirf TV/pointer environments me activate hota hai, normal desktop/mobile par
// bilkul no-op — taaki mouse/touch behaviour na bigde.
// ---------------------------------------------------------------------------

const TV_UA =
  /JioSphere|JioTV|JioFiber|JioSetTop|SmartTV|SMART-TV|HbbTV|Tizen|Web0S|webOS|BRAVIA|Android TV|AFT[BMNS]|CrKey|Roku|Viera|Hisense|Skyworth/i;

const EDGE = 64; // px from the viewport edge where auto-scroll starts
const MAX_STEP = 24; // max px per animation frame at the very edge
const FOCUSABLE = '[data-focusable], a[href], button, [role="button"]';

const isTvPointer = () => {
  if (typeof window === 'undefined') return false;
  try {
    const ua = navigator.userAgent || '';
    if (TV_UA.test(ua)) return true;

    const large = Math.min(window.screen.width, window.screen.height) >= 600;
    const touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // JioSphere/set-top boxes often send a plain "Chrome/… Android" user
    // agent (no TV marker) but have a big screen and no touch input.
    if (large && /Android/i.test(ua) && !touch) return true;

    // Other virtual-mouse TVs: coarse pointer without touch-style hover.
    const coarse =
      window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    const noHover =
      window.matchMedia && window.matchMedia('(hover: none)').matches;
    return Boolean(coarse && !noHover && large);
  } catch (e) {
    return false;
  }
};

const pageScroller = () =>
  document.scrollingElement || document.documentElement || document.body;

// Nearest ancestor that can actually scroll on the given axis (horizontal
// chip rows / rails have their own scroller; the page has the root one).
const scrollableParent = (node, axis) => {
  let el = node;
  while (el && el.nodeType === 1 && el !== document.body && el !== document.documentElement) {
    try {
      const st = window.getComputedStyle(el);
      if (axis === 'y') {
        if (
          (st.overflowY === 'auto' || st.overflowY === 'scroll') &&
          el.scrollHeight > el.clientHeight + 4
        ) {
          return el;
        }
      } else if (
        (st.overflowX === 'auto' || st.overflowX === 'scroll') &&
        el.scrollWidth > el.clientWidth + 4
      ) {
        return el;
      }
    } catch (e) {
      /* computed style unavailable */
    }
    el = el.parentElement;
  }
  return null;
};

const PointerScroll = () => {
  const location = useLocation();
  const isPlayer = location.pathname.startsWith('/watch');

  useEffect(() => {
    if (isPlayer || !isTvPointer()) return undefined;

    const root = pageScroller();
    let pointer = null; // { x, y, target }
    let rafId = 0;

    const canScrollY = (el) => el && el.scrollHeight > el.clientHeight + 4;
    const canScrollX = (el) => el && el.scrollWidth > el.clientWidth + 4;

    const step = (value) => {
      const ratio = Math.min(1, value / EDGE);
      return Math.max(1, Math.ceil(ratio * MAX_STEP));
    };

    const tick = () => {
      rafId = 0;
      if (!pointer) return;

      const vw = window.innerWidth || root.clientWidth || 0;
      const vh = window.innerHeight || root.clientHeight || 0;
      const { x, y, target } = pointer;
      let dy = 0;
      let dx = 0;

      if (y >= 0 && y < EDGE) dy = -step(EDGE - y);
      else if (y > vh - EDGE) dy = step(y - (vh - EDGE));

      if (x >= 0 && x < EDGE) dx = -step(EDGE - x);
      else if (x > vw - EDGE) dx = step(x - (vw - EDGE));

      let moved = false;

      if (dy) {
        const scroller = scrollableParent(target, 'y');
        if (scroller && canScrollY(scroller)) {
          const before = scroller.scrollTop;
          scroller.scrollTop = before + dy;
          moved = moved || scroller.scrollTop !== before;
        } else if (canScrollY(root)) {
          const before = root.scrollTop;
          root.scrollTop = before + dy;
          moved = moved || root.scrollTop !== before;
        }
      }

      if (dx) {
        const scroller = scrollableParent(target, 'x');
        if (scroller && canScrollX(scroller)) {
          const before = scroller.scrollLeft;
          scroller.scrollLeft = before + dx;
          moved = moved || scroller.scrollLeft !== before;
        }
      }

      if (moved) rafId = window.requestAnimationFrame(tick);
    };

    const schedule = () => {
      if (!rafId && pointer) rafId = window.requestAnimationFrame(tick);
    };

    const onPointerMove = (e) => {
      // Touch drags keep their native scrolling; edge auto-scroll is for the
      // virtual mouse pointer only.
      if (e.pointerType === 'touch') return;
      pointer = { x: e.clientX, y: e.clientY, target: e.target };

      // Hover focus: pointer se card/button highlight ho (D-pad ring ke bina
      // pointer users ko pata hi nahi chalta kaunsa item select ho raha hai).
      try {
        const el = e.target && e.target.closest ? e.target.closest(FOCUSABLE) : null;
        if (el && el !== document.activeElement && el.focus && !/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) {
          el.focus({ preventScroll: true });
        }
      } catch (err) {
        /* focus with options unsupported on old WebViews */
      }

      schedule();
    };

    const onPointerLeave = () => {
      pointer = null;
      if (rafId) {
        window.cancelAnimationFrame(rafId);
        rafId = 0;
      }
    };

    const onBlur = onPointerLeave;

    // Wheel: smooth-scroll whatever is under the pointer, not just the page.
    const onWheel = (e) => {
      const dy = e.deltaY || 0;
      const dx = e.deltaX || 0;
      if (!dy && !dx) return;
      e.preventDefault();

      const scroller = scrollableParent(e.target, dy ? 'y' : 'x') ||
        (dy ? (canScrollY(root) ? root : null) : null);

      const apply = (el) => {
        if (!el) return false;
        try {
          el.scrollBy({ top: dy, left: dx, behavior: 'smooth' });
          return true;
        } catch (err) {
          try {
            el.scrollTop += dy;
            el.scrollLeft += dx;
            return true;
          } catch (err2) {
            return false;
          }
        }
      };

      if (!apply(scroller) && dy) {
        try {
          window.scrollBy({ top: dy, left: 0, behavior: 'smooth' });
        } catch (err) {
          try { window.scrollBy(0, dy); } catch (err2) { /* no-op */ }
        }
      }
    };

    // Press-and-drag scrolling (virtual-mouse remotes often have no wheel).
    let drag = null;
    const onPointerDown = (e) => {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      if (e.button !== undefined && e.button !== 0) return;
      drag = {
        x: e.clientX,
        y: e.clientY,
        scrollTop: root.scrollTop,
        moved: false
      };
    };
    const onDragMove = (e) => {
      if (!drag) return;
      const dy = e.clientY - drag.y;
      const dx = e.clientX - drag.x;
      if (!drag.moved && Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      drag.moved = true;
      e.preventDefault();
      root.scrollTop = drag.scrollTop - dy;
    };
    const onDragEnd = () => {
      if (drag && drag.moved) {
        const suppress = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
        };
        window.addEventListener('click', suppress, true);
        window.setTimeout(() => {
          window.removeEventListener('click', suppress, true);
        }, 0);
      }
      drag = null;
    };

    // mouseout bubbles from every child, so it must NOT be used here — it
    // would cancel edge scrolling the moment the pointer crosses a card.
    const leaveTarget = document.documentElement || window;
    window.addEventListener('pointermove', onPointerMove, false);
    window.addEventListener('mousemove', onPointerMove, false);
    leaveTarget.addEventListener('mouseleave', onPointerLeave, false);
    window.addEventListener('blur', onBlur, false);
    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('pointerdown', onPointerDown, false);
    window.addEventListener('pointermove', onDragMove, false);
    window.addEventListener('pointerup', onDragEnd, false);
    window.addEventListener('pointercancel', onDragEnd, false);

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      window.removeEventListener('pointermove', onPointerMove, false);
      window.removeEventListener('mousemove', onPointerMove, false);
      leaveTarget.removeEventListener('mouseleave', onPointerLeave, false);
      window.removeEventListener('blur', onBlur, false);
      window.removeEventListener('wheel', onWheel, { passive: false });
      window.removeEventListener('pointerdown', onPointerDown, false);
      window.removeEventListener('pointermove', onDragMove, false);
      window.removeEventListener('pointerup', onDragEnd, false);
      window.removeEventListener('pointercancel', onDragEnd, false);
    };
  }, [isPlayer]);

  return null;
};

export default PointerScroll;
